import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';
import styles from './SendMaintenanceAttachmentsEmailModal.module.scss';
import Label from 'common/components/label/Label';
import Button from 'common/components/button/Button';
import FormItem from 'common/components/formItem/FormItem';
import Loading from 'common/services/Loading';
import { useToasts } from 'react-toast-notifications';
import Logger from 'common/services/Logger';
import { DATE_FORMAT_DEFAUT, LOGGER_LOG_TYPE, MAX_ATTACHMENT_SIZE_BYTES, MAX_ATTACHMENT_SIZE_MB, removeAccents } from 'Config';
import { MaintenanceAttachmentsEmailDto, SendMaintenanceAttachmentsEmailDto, VehicleMaintenanceDto } from 'api/vehicleMaintenances/models/VehicleMaintenanceDto';
import { Controller, useForm } from 'react-hook-form';
import InputController from 'common/components/input/InputController';
import SummernoteController, { ContextMenuOption } from 'common/components/summernoteController/SummernoteController';
import CreateSelect from 'common/components/select/CreateSelect';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import UsersService from 'api/users/UsersService';
import { UsersSelectItemDto } from 'api/users/models/UserDto';
import { ReminderContactViewModel } from 'api/reminders/models/ReminderViewModel';
import InputError from 'common/components/inputError/InputError';
import CompaniesService from 'api/companies/CompaniesService';
import FileSelector from 'common/components/fileSelector/FileSelector';
import CustomFile from 'common/models/CustomFile';
import VehicleMaintenancesService from 'api/vehicleMaintenances/VehicleMaintenancesService';
import CloseIcon from 'assets/svg/mobile_close.svg';
import ReactTooltip from 'react-tooltip';
import { FaInfoCircle } from 'react-icons/fa';
import { VehicleMaintenanceStates } from 'api/vehicleMaintenances/enums/VehicleMaintenanceStates';
import moment from 'moment';

type Props = {
    maintenance: VehicleMaintenanceDto | null;
    show: boolean;
    onCancel: () => void;
};

const SendMaintenanceAttachmentsEmailModal: React.FC<Props> = ({ maintenance, show, onCancel }: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();

    const form = useForm<SendMaintenanceAttachmentsEmailDto>({ shouldUnregister: false });
    const { handleSubmit, setValue, getValues, errors } = form;
    const [contextOptions, setContextOptions] = useState<ContextMenuOption[]>([]);

    const [responsablesToOptions, setResponsablesToOptions] = useState<SelectValueLabel[]>([]);
    const [selectEmailsTo, setSelectEmailsTo] = useState<MaintenanceAttachmentsEmailDto[]>([]);

    const [responsablesCCOptions, setResponsablesCCOptions] = useState<SelectValueLabel[]>([]);
    const [selectEmailsCC, setSelectEmailsCC] = useState<MaintenanceAttachmentsEmailDto[]>([]);

    const customStyles = {
        content: {
            top: '7%',
            left: '25%',
            right: '25%',
            bottom: '7%',
            backgroundClip: 'padding-box',
            backgroundColor: '#FFFFFF',
            border: '1px solid transparent',
            borderRadius: '4px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
            outline: '0 none',
            padding: '20px'
        },
        overlay: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flex: 1,
            zIndex: 1,
            backgroundColor: 'rgba(0, 0, 0, 0.5)'
        }
    }

    const getData = async () => {
        try {
            Loading.show();
            const [responsables, companyData] = await Promise.all([
                UsersService.getAllForCompany(),
                CompaniesService.getCurrent(),
            ]);

            const responsablesOptions = (responsables || []).map((x: UsersSelectItemDto) => ({ value: x.email || '', label: x.realName ? `${x.realName} (${x.email})` : x.email || '' }));

            setResponsablesToOptions(responsablesOptions);
            setResponsablesCCOptions(responsablesOptions);

            // email da empresa
            if (companyData?.maintenanceAttachmentsEmail) {
                const email = companyData.maintenanceAttachmentsEmail;

                // verifica se é um email que exista gravado
                const exists = responsablesOptions.some(opt => opt.value === email);
                // se não existir, adiciona manualmente
                const updatedOptions = exists ? responsablesOptions : [...responsablesOptions, { value: email, label: email }];

                setResponsablesToOptions(updatedOptions);

                // define o valor inicial
                const initialEmail = [{ email }];
                setSelectEmailsTo(initialEmail);
                form.setValue('emails_to', initialEmail);
            }
            const notificationData = await VehicleMaintenancesService.getMaintenanceEmailMessage(maintenance?.id as any);
            if(notificationData.message){
                const dateToCompare = maintenance?.state == VehicleMaintenanceStates.PREDICTED ? maintenance?.adviseMaintenanceDate : maintenance?.maintenanceDate;
                const formattedDate = dateToCompare ? moment(dateToCompare).format(DATE_FORMAT_DEFAUT) : '-';

                notificationData.message = notificationData.message.replace('@maintenanceDate', formattedDate);
            }
            
            form.setValue('message', notificationData.message);
            form.setValue('subject', notificationData.subject);

            if (maintenance?.attachments) {
                setValuesFiles(maintenance?.attachments);
            }

        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.MAINTENANCES, `Couldn't get responsables information to maintenance attachments send modal`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        } finally {
            Loading.hide();
        }
    }

    const onCreateResponsableToOption = (data: any) => {
        setResponsablesToOptions(prev => [...prev, { value: data, label: data }])
    }

    const onCreateResponsableCCOption = (data: any) => {
        setResponsablesCCOptions(prev => [...prev, { value: data, label: data }])
    }

    const setValuesFiles = (files: CustomFile[]) => {
        setValue('attachments', files);
    }

    const onSubmit = async (formData: SendMaintenanceAttachmentsEmailDto) => {
        try {
            Loading.show();

            // calcular o tamanho total dos anexos (em bytes)
            const attachments = formData?.attachments ?? [];
            const totalSize = attachments.reduce((acc, file) => acc + (file.size || 0), 0);

            // se exceder o limite (25MB), nao envia
            if (totalSize > MAX_ATTACHMENT_SIZE_BYTES) {
                Loading.hide();
                console.log('erro')
                addToast(t('common.messages.email_attachment_too_large', { limit: MAX_ATTACHMENT_SIZE_MB }), { appearance: 'warning' });
                return;
            }

            const data = {
                ...formData,
                emailTo: (formData.emails_to || []).map((x: any) => x.email).join('; '),
                emailCc: (formData.emails_cc || []).map((x: any) => x.email).join('; '),
                vehicleMaintenanceId: maintenance?.id,
                attachmentsIds: getValues('attachments').filter(x => x.id).map(x => x.id) ?? []
            }
            await VehicleMaintenancesService.sendAttachmentsEmail(data, formData?.attachments ?? []);
            addToast(t('common.messages.email_sent_success'), { appearance: 'success' });
            onCancel();

        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.MAINTENANCES, `Error send email`, error);
        } finally {
            Loading.hide();
        }
    }

    const onError = async () => {
        addToast(t('common.messages.validate_form'), { appearance: 'warning' });
    }

    useEffect(() => {
        getData();
    }, []);

    return (
        <Modal isOpen={show} style={customStyles}>
            <form onSubmit={handleSubmit(onSubmit, onError)} className={styles.formContainer}>
                <div className={styles.header}>
                    <Label className={styles.textTitle}>{t('maintenances.send_email')}</Label>
                    <div>
                        <img src={CloseIcon} onClick={() => onCancel()} className={styles.closeButton} />
                    </div>
                </div>

                <div className={styles.content}>
                    <FormItem>
                        <Label className={styles.label} required={true}>{t('common.email_to')}</Label>
                        <Controller
                            render={({ onChange, value, ref }) => {
                                return (
                                    <CreateSelect
                                        options={responsablesToOptions}
                                        innerRef={ref}
                                        isClearable
                                        isMulti
                                        placeholder={t('reminders.contacts.email')}
                                        onChange={(data: SelectValueLabel[]) => {
                                            onChange(data?.map(x => ({ email: x.value })));
                                            setSelectEmailsTo(data?.map(x => ({ email: x.value })))
                                        }}
                                        value={value ? responsablesToOptions.filter(x => value.find((y: ReminderContactViewModel) => { return y.email === x.value })) : null}
                                        filterOption={(candidate: any, input: any) => input ? removeAccents(candidate.label).toUpperCase().includes(removeAccents(input).toUpperCase()) : true}
                                        onCreateOption={(val: any) => {
                                            onCreateResponsableToOption(val);
                                            setSelectEmailsTo([...selectEmailsTo, { email: val }])
                                            onChange([...selectEmailsTo, { email: val }])
                                        }}
                                    />
                                );
                            }}
                            control={form.control}
                            name={'emails_to'}
                            rules={{ validate: { required: (value: any) => { return value ? (value && value.length > 0) : false; } } }}
                        />
                        <InputError error={errors?.emails_to as any} />
                    </FormItem>

                    <FormItem>
                        <Label className={styles.label}>{t('common.email_cc')}</Label>
                        <Controller
                            render={({ onChange, value, ref }) => {
                                return (
                                    <CreateSelect
                                        options={responsablesCCOptions}
                                        innerRef={ref}
                                        isClearable
                                        isMulti
                                        placeholder={t('reminders.contacts.email')}
                                        onChange={(data: SelectValueLabel[]) => {
                                            onChange(data?.map(x => ({ email: x.value })));
                                            setSelectEmailsCC(data?.map(x => ({ email: x.value })))
                                        }}
                                        value={value ? responsablesCCOptions.filter(x => value.find((y: ReminderContactViewModel) => { return y.email === x.value })) : null}
                                        filterOption={(candidate: any, input: any) => input ? removeAccents(candidate.label).toUpperCase().includes(removeAccents(input).toUpperCase()) : true}
                                        onCreateOption={(val: any) => {
                                            onCreateResponsableCCOption(val);
                                            setSelectEmailsCC([...selectEmailsCC, { email: val }])
                                            onChange([...selectEmailsCC, { email: val }])
                                        }}
                                    />
                                );
                            }}
                            control={form.control}
                            name={'emails_cc'}
                        />
                    </FormItem>

                    <FormItem>
                        <Label className={styles.label}>{t('notifications.subject')}</Label>
                        <InputController
                            form={form as any}
                            name={'subject'}
                            placeholder={t('notifications.subject')}
                            rules={{ maxLength: 200 }}
                        />
                    </FormItem>

                    <FormItem>
                        <Label className={styles.label} required>{t('notifications.message')}</Label>
                        <SummernoteController
                            form={form as any}
                            name={'message'}
                            placeholder={t('notifications.message')}
                            contextMenuOptions={contextOptions}
                            id={'message'}
                            required={true}
                        />
                        <InputError error={errors?.message} />
                    </FormItem>

                    <FileSelector
                        isMulti={true}
                        initialFiles={maintenance?.attachments}
                        onFilesChanged={setValuesFiles}
                        label={<>
                            <Label className={styles.label} style={{ marginBottom: '0', marginRight: '7px' }}>{t('common.add_new_file')}</Label>
                            <FaInfoCircle data-tip={t('common.messages.email_attachment_too_large', { limit: MAX_ATTACHMENT_SIZE_MB })} style={{ cursor: 'pointer' }} />
                            <ReactTooltip />
                        </>}
                        smallButtonAdd={true}
                        fileButtonSizes={{ sm: 12, lg: 6, xl: 4 }}
                    />
                </div>

                <div className={styles.buttonsContainer}>
                    <Button text={t('common.cancel')} preset={'secondary'} size={'normal'} onClick={onCancel} />
                    <Button type='submit' text={t('common.send')} size={'normal'} />
                </div>
            </form>
        </Modal>
    );
}

export default SendMaintenanceAttachmentsEmailModal;